import { useState, CSSProperties } from "react";
import { useTheme, Theme } from "../../../context/Themecontext";

// ─── Shared styles ────────────────────────────────────────────────────────────

const BASE: CSSProperties = {
  fontFamily: "'DM Sans', sans-serif",
  cursor: "pointer",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function SunIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. ThemeToggle — compact icon button (designed for topbar / sidebar footer)
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeToggleProps {
  /** Size of the button in px. Default 34 */
  size?: number;
  /** Show a tooltip on hover. Default true */
  tooltip?: boolean;
}

export function ThemeToggle({ size = 34, tooltip = true }: ThemeToggleProps) {
  const { isDark, toggleTheme } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [spinning, setSpinning] = useState(false);

  const handleClick = () => {
    setSpinning(true);
    toggleTheme();
    setTimeout(() => setSpinning(false), 400);
  };

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
        title={tooltip ? (isDark ? "Light mode" : "Dark mode") : undefined}
        style={{
          ...BASE,
          width: size,
          height: size,
          borderRadius: 9,
          border: `1px solid ${hovered ? "rgba(191,61,12,0.35)" : "var(--border)"}`,
          background: hovered ? "var(--or-dim)" : "transparent",
          color: hovered ? "var(--or)" : "var(--text3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: spinning ? "rotate(20deg)" : "rotate(0deg)",
        }}
      >
        <span style={{
          display: "flex",
          transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s",
          transform: spinning ? "scale(0.8)" : "scale(1)",
          opacity: spinning ? 0.6 : 1,
        }}>
          {isDark ? <SunIcon size={size * 0.47} /> : <MoonIcon size={size * 0.44} />}
        </span>
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. ThemeToggleSwitch — labelled pill toggle (settings page / sidebar footer)
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeToggleSwitchProps {
  /** Show "Dark mode" label. Default true */
  showLabel?: boolean;
}

export function ThemeToggleSwitch({ showLabel = true }: ThemeToggleSwitchProps) {
  const { isDark, toggleTheme } = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={toggleTheme}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="switch"
      aria-checked={isDark}
      aria-label="Toggle dark mode"
      style={{
        ...BASE,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        borderRadius: 10,
        border: `1px solid ${hovered ? "rgba(191,61,12,0.2)" : "var(--border)"}`,
        background: hovered ? "var(--or-dim)" : "transparent",
        userSelect: "none",
      }}
    >
      {/* Icon */}
      <span style={{ color: isDark ? "var(--or)" : "var(--text3)", display: "flex", transition: "color 0.2s" }}>
        {isDark ? <MoonIcon size={15} /> : <SunIcon size={15} />}
      </span>

      {/* Label */}
      {showLabel && (
        <span style={{
          fontSize: 14,
          fontWeight: 500,
          color: "var(--text2)",
          flex: 1,
          whiteSpace: "nowrap",
          transition: "color 0.2s",
        }}>
          {isDark ? "Dark mode" : "Light mode"}
        </span>
      )}

      {/* Track */}
      <div style={{
        width: 34,
        height: 20,
        borderRadius: 10,
        background: isDark ? "var(--or)" : "var(--border-strong)",
        position: "relative",
        flexShrink: 0,
        transition: "background 0.25s",
        border: `1px solid ${isDark ? "rgba(191,61,12,0.4)" : "var(--border-strong)"}`,
      }}>
        {/* Thumb */}
        <div style={{
          position: "absolute",
          top: 2,
          left: isDark ? 14 : 2,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
          transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
        }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. ThemeToggleSegmented — Dark | System | Light three-way picker
// ─────────────────────────────────────────────────────────────────────────────

type ThemeSegment = Theme | "system";

interface ThemeToggleSegmentedProps {
  /** Show text labels. Default true */
  showLabels?: boolean;
}

export function ThemeToggleSegmented({ showLabels = true }: ThemeToggleSegmentedProps) {
  const { setTheme } = useTheme();
  const [active, setActive] = useState<ThemeSegment>(() => {
    try {
      return (localStorage.getItem("aural-theme") as ThemeSegment) ?? "system";
    } catch {
      return "system";
    }
  });

  const handleSelect = (seg: ThemeSegment) => {
    setActive(seg);
    if (seg === "system") {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      setTheme(prefersDark ? "dark" : "light");
      try { localStorage.removeItem("aural-theme"); } catch { /* noop */ }
    } else {
      setTheme(seg);
    }
  };

  const segments: { id: ThemeSegment; icon: React.ReactNode; label: string }[] = [
    { id: "dark",   icon: <MoonIcon size={13} />,    label: "Dark"   },
    { id: "system", icon: <MonitorIcon size={13} />, label: "System" },
    { id: "light",  icon: <SunIcon size={13} />,     label: "Light"  },
  ];

  return (
    <div style={{
      display: "inline-flex",
      background: "var(--input-bg)",
      border: "1px solid var(--border)",
      borderRadius: 10,
      padding: 3,
      gap: 2,
    }}>
      {segments.map((seg) => {
        const isActive = active === seg.id;
        return (
          <button
            key={seg.id}
            onClick={() => handleSelect(seg.id)}
            aria-pressed={isActive}
            aria-label={`${seg.label} theme`}
            style={{
              ...BASE,
              display: "flex",
              alignItems: "center",
              gap: showLabels ? 5 : 0,
              padding: showLabels ? "6px 12px" : "7px 10px",
              borderRadius: 7,
              border: `1px solid ${isActive ? "rgba(191,61,12,0.3)" : "transparent"}`,
              background: isActive ? "var(--or-dim)" : "transparent",
              color: isActive ? "var(--or)" : "var(--text3)",
              fontSize: 12,
              fontWeight: isActive ? 600 : 400,
            }}
          >
            {seg.icon}
            {showLabels && <span>{seg.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. ThemeToggleCard — rich settings card with all three segments + preview
// ─────────────────────────────────────────────────────────────────────────────

export function ThemeToggleCard() {
  const { isDark } = useTheme();

  return (
    <div style={{
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 14,
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 20px",
        borderBottom: "1px solid var(--border)",
        background: "var(--section-header)",
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Appearance</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>
            Choose your preferred colour scheme
          </div>
        </div>
        <ThemeToggle size={34} />
      </div>

      {/* Segmented picker */}
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>
          Theme
        </div>
        <ThemeToggleSegmented />
      </div>

      {/* Mini preview */}
      <div style={{ padding: "16px 20px" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>
          Preview
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {/* Dark swatch */}
          <div
            onClick={() => {}}
            style={{
              borderRadius: 10,
              overflow: "hidden",
              border: `2px solid ${isDark ? "var(--or)" : "transparent"}`,
              cursor: "pointer",
              transition: "border-color 0.2s",
            }}
          >
            <div style={{ background: "#111110", padding: 10, height: 72, position: "relative" }}>
              <div style={{ width: "50%", height: 4, background: "rgba(255,255,255,0.12)", borderRadius: 2, marginBottom: 5 }} />
              <div style={{ width: "80%", height: 3, background: "rgba(255,255,255,0.07)", borderRadius: 2, marginBottom: 4 }} />
              <div style={{ width: "65%", height: 3, background: "rgba(255,255,255,0.07)", borderRadius: 2, marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 4 }}>
                <div style={{ height: 16, flex: 1, background: "rgba(255,255,255,0.06)", borderRadius: 4 }} />
                <div style={{ height: 16, flex: 1, background: "#BF3D0C", borderRadius: 4, opacity: 0.9 }} />
              </div>
            </div>
            <div style={{ background: "#0e0d0c", padding: "5px 10px", display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#BF3D0C" }} />
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Dark</span>
              {isDark && <span style={{ fontSize: 11, color: "#BF3D0C", marginLeft: "auto" }}>✓</span>}
            </div>
          </div>

          {/* Light swatch */}
          <div
            onClick={() => {}}
            style={{
              borderRadius: 10,
              overflow: "hidden",
              border: `2px solid ${!isDark ? "var(--or)" : "transparent"}`,
              cursor: "pointer",
              transition: "border-color 0.2s",
            }}
          >
            <div style={{ background: "#f5f4f0", padding: 10, height: 72, position: "relative" }}>
              <div style={{ width: "50%", height: 4, background: "rgba(0,0,0,0.12)", borderRadius: 2, marginBottom: 5 }} />
              <div style={{ width: "80%", height: 3, background: "rgba(0,0,0,0.07)", borderRadius: 2, marginBottom: 4 }} />
              <div style={{ width: "65%", height: 3, background: "rgba(0,0,0,0.07)", borderRadius: 2, marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 4 }}>
                <div style={{ height: 16, flex: 1, background: "rgba(0,0,0,0.06)", borderRadius: 4 }} />
                <div style={{ height: 16, flex: 1, background: "#BF3D0C", borderRadius: 4, opacity: 0.9 }} />
              </div>
            </div>
            <div style={{ background: "#ffffff", padding: "5px 10px", display: "flex", alignItems: "center", gap: 5, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#BF3D0C" }} />
              <span style={{ fontSize: 11, color: "rgba(0,0,0,0.4)" }}>Light</span>
              {!isDark && <span style={{ fontSize: 11, color: "#BF3D0C", marginLeft: "auto" }}>✓</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}